import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { Fragment, useMemo } from "react";
import { twMerge } from "tailwind-merge";

import type { FileStatusType, FileUploadAndParseProps } from "@fronterahealth/frontera-ui-components";
import { Paragraph, SelectItem, Tooltip } from "@fronterahealth/frontera-ui-components";

import {
  ApiLearnerFileFileKindChoices,
  ApiLearnerFileProcessingFailureCodeChoices,
  ApiReportReportTypeChoices,
  ApiReportTemplateFileUploadConfigChoices,
  AssessmentToolType,
} from "@api/graphql/types-and-hooks";
import { FileTableRow } from "@components/UploadDocuments/FileTableRow";
import {
  AssessmentSelectItem,
  computeFileTypeErrors,
  nonNullStrings,
} from "@components/UploadDocuments/fileTypeSupport";
import { expandInterviewOptions } from "@components/UploadDocuments/interviewType";
import { useAdminData } from "@providers/AdminDataProvider";

const ASSESSMENT = "ASSESSMENT";

const BASE_DOCUMENT_TYPE_OPTIONS: SelectItem[] = [
  { id: ApiLearnerFileFileKindChoices.Interview, primary: "Interview" },
  { id: ApiLearnerFileFileKindChoices.BehavioralObservations, primary: "Behavioral Observation" },
  { id: ASSESSMENT, primary: "Assessment" },
  { id: ApiLearnerFileFileKindChoices.ClinicalNotes, primary: "Supporting Document" },
];

const DB_CATALIGHT_DOCUMENT_TYPE_OPTIONS: SelectItem[] = [
  { id: ApiLearnerFileFileKindChoices.Interview, primary: "Interview" },
  { id: ApiLearnerFileFileKindChoices.CatalightIntake, primary: "Catalight Intake" },
  { id: ApiLearnerFileFileKindChoices.BehavioralObservations, primary: "Behavioral Observation" },
  { id: ASSESSMENT, primary: "Assessment" },
  { id: ApiLearnerFileFileKindChoices.ClinicalNotes, primary: "Supporting Document" },
];

const DB_ABC_KUWAIT_DOCUMENT_TYPE_OPTIONS: SelectItem[] = [
  { id: ApiLearnerFileFileKindChoices.Interview, primary: "Interview" },
];

const DB_ABC_DOCUMENT_TYPE_OPTIONS: SelectItem[] = [
  { id: ApiLearnerFileFileKindChoices.ActionBehavioralCenterIntake, primary: "ABC Intake" },
  { id: ASSESSMENT, primary: "Assessment" },
  { id: ApiLearnerFileFileKindChoices.Interview, primary: "Interview" },
];

const RA_DOCUMENT_TYPE_OPTIONS: SelectItem[] = [
  { id: ASSESSMENT, primary: "Assessment" },
  { id: ApiLearnerFileFileKindChoices.PreviousAssessment, primary: "Prior Report" },
];

const IA_DOCUMENT_TYPE_OPTIONS: SelectItem[] = [
  { id: ApiLearnerFileFileKindChoices.Interview, primary: "Interview" },
  { id: ApiLearnerFileFileKindChoices.DiagnosisReport, primary: "Diagnosis Report" },
  { id: ApiLearnerFileFileKindChoices.BehavioralObservations, primary: "Behavioral Observation" },
  { id: ASSESSMENT, primary: "Assessment" },
  { id: ApiLearnerFileFileKindChoices.ClinicalNotes, primary: "Clinical Notes" },
];

const IA_ACES_DOCUMENT_TYPE_OPTIONS: SelectItem[] = [
  { id: ApiLearnerFileFileKindChoices.Interview, primary: "Interview Recording" },
  { id: ApiLearnerFileFileKindChoices.BehavioralObservations, primary: "Interview & Observation Notes" },
  { id: ApiLearnerFileFileKindChoices.Bhtia, primary: "BHT/Intake Form" },
  { id: ApiLearnerFileFileKindChoices.DiagnosisReport, primary: "Diagnosis Report" },
  { id: ASSESSMENT, primary: "Assessment" },
];

const IA_CATALIGHT_DOCUMENT_TYPE_OPTIONS: SelectItem[] = [
  { id: ApiLearnerFileFileKindChoices.Interview, primary: "Interview Recording" },
  { id: ApiLearnerFileFileKindChoices.BehavioralObservations, primary: "Interview & Observation Notes" },
  { id: ApiLearnerFileFileKindChoices.Bhtia, primary: "BHTIA" },
  { id: ApiLearnerFileFileKindChoices.DiagnosisReport, primary: "Diagnosis Report" },
  { id: ASSESSMENT, primary: "Assessment" },
];

interface FileTableProps {
  files: Array<{
    fileName: string;
    fileId: string;
    fileSize: number;
    fileStatus: FileStatusType;
    failureReason?: string;
    failureCode?: ApiLearnerFileProcessingFailureCodeChoices | null;
    fileKind?: string;
    mimeType?: string;
    metadata?: string | null;
  }>;
  supportedAssessments: AssessmentToolType[];
  removeFileCallback: FileUploadAndParseProps["removeFileCallback"];
  downloadFileCallback?: FileUploadAndParseProps["downloadFileCallback"];
  previewFileCallback?: (fileId: string) => Promise<void>;
  allowFileDownload?: boolean;
  onResetFile: (fileName: string) => void;
  onFileUpdated?: (fileName: string, fileId: string) => void;
  reportType: ApiReportReportTypeChoices | undefined;
  fileUploadConfig: ApiReportTemplateFileUploadConfigChoices | undefined;
  reportId?: string;
  learnerId?: string;
}

export const FileTable: React.FC<FileTableProps> = ({
  files,
  supportedAssessments,
  removeFileCallback,
  downloadFileCallback,
  previewFileCallback,
  allowFileDownload,
  onResetFile,
  onFileUpdated,
  reportType,
  fileUploadConfig,
  reportId,
  learnerId,
}) => {
  const { isABCUser, isAcesUser, isTestAcesUser, isKuwaitUser } = useAdminData();
  // Composite "Interview - Parent/Child/Teacher/Other" options only for orgs whose report
  // prompts consume those buckets (gated to AT4K initially). Everyone else keeps the single
  // generic "Interview", which routes to interviews.other as before.
  const { "interview-type-classification": interviewTypeClassificationEnabled } = useFlags<FeatureFlags>();

  // Build document type options based on variant or organization
  const documentTypeOptions = useMemo(() => {
    const base: SelectItem[] = (() => {
      if (reportType === ApiReportReportTypeChoices.Reassessment) {
        return RA_DOCUMENT_TYPE_OPTIONS;
      }
      if (reportType === ApiReportReportTypeChoices.InitialAssessment) {
        if (isAcesUser || isTestAcesUser) {
          return IA_ACES_DOCUMENT_TYPE_OPTIONS;
        }
        if (fileUploadConfig === ApiReportTemplateFileUploadConfigChoices.Catalight) {
          return IA_CATALIGHT_DOCUMENT_TYPE_OPTIONS;
        }
        return IA_DOCUMENT_TYPE_OPTIONS;
      }
      if (reportType === ApiReportReportTypeChoices.DiagnosisEvaluation) {
        if (isABCUser) {
          return DB_ABC_DOCUMENT_TYPE_OPTIONS;
        }
        if (isKuwaitUser) {
          return DB_ABC_KUWAIT_DOCUMENT_TYPE_OPTIONS;
        }
        if (fileUploadConfig === ApiReportTemplateFileUploadConfigChoices.Catalight) {
          return DB_CATALIGHT_DOCUMENT_TYPE_OPTIONS;
        }
      }
      return BASE_DOCUMENT_TYPE_OPTIONS;
    })();
    return interviewTypeClassificationEnabled ? expandInterviewOptions(base) : base;
  }, [
    reportType,
    isABCUser,
    isAcesUser,
    isTestAcesUser,
    isKuwaitUser,
    fileUploadConfig,
    interviewTypeClassificationEnabled,
  ]);

  // Create assessment type options from supportedAssessments, sorted alphabetically
  const assessmentTypeOptions = useMemo<AssessmentSelectItem[]>(
    () =>
      supportedAssessments
        .map((assessment) => ({
          id: assessment.id || "",
          primary: assessment.primary || "",
          secondary: assessment.secondary || "",
          supportedFileTypes: nonNullStrings(assessment.supportedFileTypes),
        }))
        .sort((a, b) => a.primary.localeCompare(b.primary)),
    [supportedAssessments],
  );

  if (!files || files.length === 0) return null;

  const rowClassName = "grid-cols-[2.5fr_4rem_1fr_2fr_2fr_5rem]";

  return (
    <div className="border border-border-strong rounded-lg w-full">
      {/* Table Header */}
      <div
        className={twMerge(
          "grid gap-4 px-4 py-3 bg-limestone-50 border-b border-border-strong text-limestone-900",
          rowClassName,
        )}
      >
        <Paragraph displayType="loud" size="extra-small">
          File Name
        </Paragraph>
        <div></div>
        <Paragraph displayType="loud" size="extra-small">
          Status
        </Paragraph>
        <Paragraph displayType="loud" size="extra-small">
          Document Type
        </Paragraph>
        <div className="flex items-center gap-1">
          <Paragraph displayType="loud" size="extra-small">
            Document Properties
          </Paragraph>
          <Tooltip text="Adjust properties for this document. Options vary by document type" />
        </div>
        <div></div>
      </div>

      {/* Table Body */}
      {files.map((file) => {
        const errorStrings = computeFileTypeErrors(file, assessmentTypeOptions).map((item) => item.message);
        return (
          <Fragment key={file.fileName}>
            <FileTableRow
              className={rowClassName}
              fileName={file.fileName}
              fileId={file.fileId}
              fileSize={file.fileSize}
              fileStatus={file.fileStatus}
              failureReason={file.failureReason}
              failureCode={file.failureCode}
              fileKind={file.fileKind}
              mimeType={file.mimeType}
              metadata={file.metadata}
              removeFileCallback={removeFileCallback}
              downloadFileCallback={downloadFileCallback}
              previewFileCallback={previewFileCallback}
              allowFileDownload={allowFileDownload}
              resetFile={() => onResetFile(file.fileName)}
              assessmentTypeOptions={assessmentTypeOptions}
              documentTypeOptions={documentTypeOptions}
              onFileUpdated={onFileUpdated}
              reportId={reportId}
              reportType={reportType}
              learnerId={learnerId}
            />
            {!!errorStrings.length && (
              <div className="flex flex-row gap-3 items-start px-2 py-2 border-b text-red-700 text-sm bg-static-fill-background">
                <ExclamationTriangleIcon className="size-5" />
                <div>
                  {errorStrings.map((item) => (
                    <div key={item}>{item}</div>
                  ))}
                </div>
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
};
